/**
 * Redux async actions (src/store/actions/evaluationActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

// src/store/actions/evaluationActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

// ===== EXISTING EVALUATION ACTIONS (UPDATED WITH LANG PARAMETER) =====

/**
 * Async thunk: createEvaluation.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createEvaluation = createAsyncThunk(
  "evaluations/createEvaluation",
  async (evaluationData) => {
    try {
      const response = await axios.post(`evaluations`, evaluationData);
      return response.data.evaluation;
    } catch (error) {
      throw error.response?.data?.message || "Failed to create evaluation";
    }
  }
);

/**
 * Async thunk: getAllEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAllEvaluations = createAsyncThunk(
  "evaluations/getAllEvaluations",
  async (lang = 'en') => {
    try {
      const response = await axios.get(`evaluations?lang=${lang}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch evaluations";
    }
  }
);



// ✅ CORRECT - Get Student Evaluations
/**
 * Async thunk: getStudentEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
// This is a client cache boundary, never server authorization. A different
// signed-in principal must not inherit the same child/language cache.
export const getStudentEvaluationScopeKey = ({ actorId, studentId, lang = 'en' } = {}) => (
  JSON.stringify([String(actorId || ''), String(studentId || ''), String(lang || 'en')])
);

// Learner loads expose a typed public error, never upstream details or a raw toast.
export const normalizeStudentEvaluationError = (failure = {}) => {
  const error = failure && typeof failure === 'object' ? failure : {};
  const rawStatus = error.response?.status ?? error.status;
  const status = Number.isInteger(rawStatus) && rawStatus >= 400 && rawStatus <= 599 ? rawStatus : null;
  const kind = error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error.kind === 'aborted' ? 'aborted'
    : status === 401 ? 'unauthorized' : status === 403 ? 'forbidden' : status === 404 ? 'unavailable'
    : error.code === 'INVALID_STUDENT_ID' || error.kind === 'invalidInput' ? 'invalidInput'
    : error.code === 'INVALID_EVALUATIONS_RESPONSE' || error.kind === 'invalidResponse' ? 'invalidResponse'
    : status >= 500 ? 'service' : status ? 'request' : 'network';
  const messageKey = kind === 'forbidden' ? 'evaluation.accessDenied'
    : kind === 'unauthorized' ? 'evaluation.sessionRequired'
    : kind === 'unavailable' ? 'evaluation.unavailable'
    : kind === 'invalidResponse' ? 'evaluation.invalidResponse' : 'evaluation.loadError';
  return { status, kind, messageKey };
};

export const getStudentEvaluations = createAsyncThunk(
  'evaluations/getStudentEvaluations',
  async ({ studentId, lang = 'en', page = 1, limit = 10 }, { rejectWithValue, signal }) => {
    try {
      // Debug logging
      
      
      // Validation
      if (typeof studentId !== 'string' || !studentId.trim() || studentId === 'undefined') {
        return rejectWithValue(normalizeStudentEvaluationError({ code: 'INVALID_STUDENT_ID' }));
      }
      
      // Construct URL
      const queryParams = new URLSearchParams({ lang, page: String(page), limit: String(limit) });
      const url = `evaluations/student/${studentId}?${queryParams.toString()}`;
     
      
      // Make API call
      const response = await axios.get(url, { signal });
      
      
      return response.data;
      
    } catch (error) {
      return rejectWithValue(normalizeStudentEvaluationError(signal.aborted ? { kind: 'aborted' } : error));
    }
  }
);

// ✅ CORRECT - Get Evaluation Stats  

/**
 * Async thunk: getTeacherEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherEvaluations = createAsyncThunk(
  "evaluations/getTeacherEvaluations",
  async ({ teacherId, lang = 'en', page, limit }, { rejectWithValue, signal }) => {
    try {
      const normalizedTeacherId = String(teacherId || '').trim();
      if (!normalizedTeacherId) {
        return rejectWithValue({
          message: 'Teacher ID is required',
          status: null,
          data: null,
        });
      }

      // The deployed endpoint rejects the old 200-row request. Use a
      // conservative server-supported page and aggregate only when the caller
      // asks for the complete teacher collection.
      const requestedLimit = Number(limit);
      const pageLimit = Number.isSafeInteger(requestedLimit) && requestedLimit > 0
        ? Math.min(requestedLimit, 50)
        : 50;
      const requestedPage = Number(page);
      const hasExplicitPage = Number.isSafeInteger(requestedPage) && requestedPage > 0;
      const loadPage = async (pageNumber) => {
        const queryParams = new URLSearchParams({
          lang,
          page: String(pageNumber),
          limit: String(pageLimit),
        });
        const response = await axios.get(
          `evaluations/teacher/${normalizedTeacherId}?${queryParams.toString()}`,
          { signal }
        );
        return response.data;
      };

      if (hasExplicitPage) {
        return await loadPage(requestedPage);
      }

      const evaluations = [];
      const evaluationIds = new Set();
      let firstPayload = null;
      let expectedPages = null;
      let expectedTotal = null;

      for (let pageNumber = 1; expectedPages === null || pageNumber <= expectedPages; pageNumber += 1) {
        if (signal?.aborted) {
          return rejectWithValue({
            message: 'Teacher evaluations request was cancelled',
            status: null,
            data: null,
          });
        }

        const payload = await loadPage(pageNumber);
        const responsePage = payload?.page;
        const responsePages = payload?.pages;
        const responseTotal = payload?.total;
        const pageEvaluations = payload?.evaluations;
        const advertisedPages = responseTotal === 0
          ? 0
          : Math.ceil(responseTotal / pageLimit);
        const expectedPageLength = advertisedPages === 0
          ? 0
          : pageNumber < advertisedPages
            ? pageLimit
            : responseTotal - (pageLimit * (advertisedPages - 1));

        if (
          !Array.isArray(pageEvaluations)
          || !Number.isSafeInteger(responsePage)
          || responsePage !== pageNumber
          || !Number.isSafeInteger(responsePages)
          || responsePages < 0
          || !Number.isSafeInteger(responseTotal)
          || responseTotal < 0
          || responsePages !== advertisedPages
          || pageEvaluations.length !== expectedPageLength
          || (expectedPages !== null && responsePages !== expectedPages)
          || (expectedTotal !== null && responseTotal !== expectedTotal)
        ) {
          return rejectWithValue({
            message: 'Invalid teacher evaluation pagination response',
            status: null,
            data: null,
          });
        }

        firstPayload = firstPayload || payload;
        expectedPages = responsePages;
        expectedTotal = responseTotal;

        for (const evaluation of pageEvaluations) {
          const evaluationId = String(evaluation?._id || evaluation?.id || '');
          if (!evaluationId || evaluationIds.has(evaluationId)) {
            return rejectWithValue({
              message: 'Invalid teacher evaluation pagination response',
              status: null,
              data: null,
            });
          }
          evaluationIds.add(evaluationId);
          evaluations.push(evaluation);
        }

        if (evaluations.length > expectedTotal) {
          return rejectWithValue({
            message: 'Invalid teacher evaluation pagination response',
            status: null,
            data: null,
          });
        }
      }

      if (!firstPayload || evaluations.length !== expectedTotal) {
        return rejectWithValue({
          message: 'Invalid teacher evaluation pagination response',
          status: null,
          data: null,
        });
      }

      return {
        ...firstPayload,
        evaluations,
        count: evaluations.length,
        page: 1,
        pages: expectedPages,
        pagination: {
          currentPage: 1,
          totalPages: expectedPages,
          totalItems: expectedTotal,
          itemsPerPage: pageLimit,
          loadedPages: expectedPages === 0 ? 1 : expectedPages,
          isComplete: true,
        },
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to fetch teacher evaluations",
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

/**
 * Async thunk: getEvaluation.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getEvaluation = createAsyncThunk(
  "evaluations/getEvaluation",
  async ({ id, lang = 'en' }) => {
    try {
      const response = await axios.get(`evaluations/${id}?lang=${lang}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch evaluation";
    }
  }
);

/**
 * Async thunk: updateEvaluation.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateEvaluation = createAsyncThunk(
  "evaluations/updateEvaluation",
  async ({ id, updatedData }) => {
    try {
      const response = await axios.put(`evaluations/${id}`, updatedData);
      return response.data.evaluation;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update evaluation";
    }
  }
);

/**
 * Async thunk: deleteEvaluation.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deleteEvaluation = createAsyncThunk(
  "evaluations/deleteEvaluation",
  async (id) => {
    try {
      const response = await axios.delete(`evaluations/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete evaluation";
    }
  }
);

/**
 * Async thunk: getEvaluationStats.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getEvaluationStats = createAsyncThunk(
  'evaluations/getEvaluationStats',
  async ({ studentId, lang = 'en' }, { rejectWithValue, signal }) => {
    try {
      // Debug logging
      
      
      // Validation
      if (typeof studentId !== 'string' || !studentId.trim() || studentId === 'undefined') {
        return rejectWithValue(normalizeStudentEvaluationError({ code: 'INVALID_STUDENT_ID' }));
      }
      
      // Construct URL
      const url = `evaluations/stats/${studentId}?lang=${lang}`;
      
      
      // Make API call
      const response = await axios.get(url, { signal });
      
      
      return response.data;
      
    } catch (error) {
      return rejectWithValue(normalizeStudentEvaluationError(signal.aborted ? { kind: 'aborted' } : error));
    }
  }
);




// ===== NEW ADMIN EVALUATION ACTIONS =====

/**
 * Async thunk: getAllEvaluationsAdmin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAllEvaluationsAdmin = createAsyncThunk(
  "evaluations/getAllEvaluationsAdmin",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add all possible query parameters
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const response = await axios.get(`admin/evaluations?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch admin evaluations";
    }
  }
);

// Admin-only CRUD actions. Keep these separate from the student/teacher
// evaluation actions above so role-specific endpoints cannot be mixed.
export const createEvaluationAdmin = createAsyncThunk(
  "evaluations/createEvaluationAdmin",
  async (evaluationData) => {
    try {
      const response = await axios.post('admin/evaluations', evaluationData);
      return response.data.evaluation;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create admin evaluation';
    }
  }
);

export const getEvaluationAdmin = createAsyncThunk(
  "evaluations/getEvaluationAdmin",
  async (id) => {
    try {
      const response = await axios.get(`admin/evaluations/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch admin evaluation';
    }
  }
);

export const updateEvaluationAdmin = createAsyncThunk(
  "evaluations/updateEvaluationAdmin",
  async ({ id, updatedData }) => {
    try {
      const response = await axios.put(`admin/evaluations/${id}`, updatedData);
      return response.data.evaluation;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update admin evaluation';
    }
  }
);

export const deleteEvaluationAdmin = createAsyncThunk(
  "evaluations/deleteEvaluationAdmin",
  async (id) => {
    try {
      const response = await axios.delete(`admin/evaluations/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete admin evaluation';
    }
  }
);

/**
 * Async thunk: searchEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const searchEvaluations = createAsyncThunk(
  "evaluations/searchEvaluations",
  async (params) => {
    try {
      const { searchTerm, page = 1, limit = 10 } = params;
      const response = await axios.get(`admin/evaluations/search?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to search evaluations";
    }
  }
);

/**
 * Async thunk: getEvaluationStatsAdmin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getEvaluationStatsAdmin = createAsyncThunk(
  "evaluations/getEvaluationStatsAdmin",
  async (timeRange = '30d') => {
    try {
      const response = await axios.get(`admin/evaluations/stats?timeRange=${timeRange}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch admin evaluation stats";
    }
  }
);

/**
 * Async thunk: getEvaluationAnalytics.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getEvaluationAnalytics = createAsyncThunk(
  "evaluations/getEvaluationAnalytics",
  async () => {
    try {
      const response = await axios.get(`admin/evaluations/analytics`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch evaluation analytics";
    }
  }
);

/**
 * Async thunk: exportEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportEvaluations = createAsyncThunk(
  "evaluations/exportEvaluations",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const response = await axios.get(`admin/evaluations/export?${queryParams.toString()}`, {
        responseType: params.format === 'csv' ? 'blob' : 'json'
      });
      
      if (params.format === 'csv') {
        // Create a blob URL for CSV download
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `evaluations_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'CSV file downloaded successfully' };
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to export evaluations";
    }
  }
);

/**
 * Async thunk: bulkDeleteEvaluations.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const bulkDeleteEvaluations = createAsyncThunk(
  "evaluations/bulkDeleteEvaluations",
  async (evaluationIds) => {
    try {
      const response = await axios.delete(`admin/evaluations/bulk-delete`, {
        data: { evaluationIds }
      });
      return { evaluationIds, message: response.data.message };
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete evaluations";
    }
  }
);

// ===== TRIAL PREFERENCES ACTIONS =====

/**
 * Async thunk: getAllTrialPreferences.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAllTrialPreferences = createAsyncThunk(
  "evaluations/getAllTrialPreferences",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const response = await axios.get(`admin/trial-preferences?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch trial preferences";
    }
  }
);

/**
 * Async thunk: updateTrialPreferences.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateTrialPreferences = createAsyncThunk(
  "evaluations/updateTrialPreferences",
  async ({ id, updateData }) => {
    try {
      const response = await axios.put(`admin/trial-preferences/${id}`, updateData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update trial preferences";
    }
  }
);

/**
 * Async thunk: deleteTrialPreferences.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deleteTrialPreferences = createAsyncThunk(
  "evaluations/deleteTrialPreferences",
  async (id) => {
    try {
      const response = await axios.delete(`admin/trial-preferences/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete trial preferences";
    }
  }
);
